import React, { useState } from 'react';
import { Button, Alert } from 'react-bootstrap';
import { FormattedMessage } from 'react-intl';
import { useMutation } from '@apollo/client';
import { mutations, queriesV2 } from '../../../api';
import { useNotifications } from '../../../notifications';
import { TitleFormattedMessage } from '../../../components/titleFormattedMessage';
import PropdocsModal from '../../../components/modal/PropdocsModal';

type DeleteClauseFolderModalProps = {
  isOpen: boolean;
  onClose: () => void;
  folderId: string;
  folderName: string;
  teamId: string;
  onSuccess: () => void;
  hasChildren?: boolean;
};

const DeleteClauseFolderModal = ({
  isOpen,
  onClose,
  folderId,
  folderName,
  teamId,
  onSuccess,
  hasChildren = false,
}: DeleteClauseFolderModalProps) => {
  const [confirmationStep, setConfirmationStep] = useState<'initial' | 'final'>('initial');
  const [isDeleting, setIsDeleting] = useState(false);
  const [, { success: notifySuccess, error: notifyError }] = useNotifications();

  const [deleteTemplateFolder] = useMutation(mutations.deleteTemplateFolder as any, {
    onCompleted: () => {
      notifySuccess({ message: 'Clause folder deleted successfully' });
      onSuccess();
      handleClose();
      setIsDeleting(false);
    },
    onError: (error) => {
      notifyError({ message: error.message || 'Failed to delete clause folder' });
      setIsDeleting(false);
    },
  });

  const handleClose = () => {
    setConfirmationStep('initial');
    setIsDeleting(false);
    onClose();
  };

  const handleContinue = () => {
    setConfirmationStep('final');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      await deleteTemplateFolder({
        variables: {
          id: folderId,
          force: true, // Force delete with contents
        },
        refetchQueries: [
          {
            query: queriesV2.getTemplateFolders,
            variables: { teamId, folderType: 'clause' },
          },
        ],
      });
    } catch (error) {
      console.error('Error deleting clause folder:', error);
      notifyError({ message: 'Failed to delete clause folder' });
      setIsDeleting(false);
    }
  };

  const renderInitialConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="message.deleting-clause-folder"
              defaultMessage="Deleting: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="warning">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="warning.title" defaultMessage="Warning" />
            </strong>
          </p>
          <p>
            {hasChildren ? (
              <FormattedMessage
                id="clauses.folders.delete.warning.with-contents"
                defaultMessage="This clause folder contains subfolders and/or clauses. Deleting this folder will archive all clauses (move them to the root level and hide them) and permanently delete all subfolders."
              />
            ) : (
              <FormattedMessage
                id="clauses.folders.delete.warning.empty"
                defaultMessage="This action will permanently delete the clause folder."
              />
            )}
          </p>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="clauses.folders.delete.warning.irreversible"
                defaultMessage="This action cannot be undone."
              />
            </strong>
          </p>
        </Alert>

        <p>
          <FormattedMessage
            id="clauses.folders.delete.continue-question"
            defaultMessage="Are you sure you want to continue?"
          />
        </p>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="warning" onClick={handleContinue}>
          <TitleFormattedMessage id="cta.continue" defaultMessage="Continue" />
        </Button>
      </div>
    </>
  );

  const renderFinalConfirmation = () => (
    <>
      <div style={{ marginBottom: '1.5rem' }}>
        <div className="mb-3">
          <strong>
            <FormattedMessage
              id="message.confirm-deleting-clause-folder"
              defaultMessage="Confirm deletion of: {folderName}"
              values={{ folderName }}
            />
          </strong>
        </div>

        <Alert variant="danger">
          <p className="mb-2">
            <strong>
              <FormattedMessage id="danger.final-warning" defaultMessage="Final Warning" />
            </strong>
          </p>
          <p>
            <FormattedMessage
              id="clauses.folders.delete.final-warning"
              defaultMessage="You are about to delete this clause folder. Any clauses inside will be archived (moved to root level and hidden - you can reactivate them later). Subfolders will be permanently deleted."
            />
          </p>
          <p className="mb-0">
            <strong>
              <FormattedMessage
                id="clauses.folders.delete.are-you-sure"
                defaultMessage="Are you sure you want to delete this clause folder and archive its clauses?"
              />
            </strong>
          </p>
        </Alert>
      </div>
      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '1rem', paddingTop: '1.5rem' }}>
        <Button variant="link" className="text-secondary" onClick={handleClose} disabled={isDeleting}>
          <TitleFormattedMessage id="cta.cancel" defaultMessage="Cancel" />
        </Button>
        <Button variant="danger" onClick={handleDelete} disabled={isDeleting}>
          {isDeleting ? (
            <FormattedMessage id="cta.deleting" defaultMessage="Deleting..." />
          ) : (
            <TitleFormattedMessage id="cta.delete" defaultMessage="Delete" />
          )}
        </Button>
      </div>
    </>
  );

  return (
    <PropdocsModal
      show={isOpen}
      setShow={handleClose}
      size="lg"
      disableClose={isDeleting}
      title={
        confirmationStep === 'initial' ? (
          <FormattedMessage id="title.delete-clause-folder" defaultMessage="Delete Clause Folder" />
        ) : (
          <FormattedMessage id="title.confirm-delete-clause-folder" defaultMessage="Confirm Clause Folder Deletion" />
        )
      }
    >
      {confirmationStep === 'initial' ? renderInitialConfirmation() : renderFinalConfirmation()}
    </PropdocsModal>
  );
};

export default DeleteClauseFolderModal;
